import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
} from '@mui/material';
import { GoogleMap, LoadScript, Marker, InfoWindow } from '@react-google-maps/api';
import {
  DirectionsCar as VehicleIcon,
  Assignment as JobIcon,
  LocalGasStation as DepotIcon,
} from '@mui/icons-material';
import Layout from '../../components/Layout';
import api from '../../services/api';
import { MAP_MARKER_COLORS } from '../../utils/mapMarkerColors';
import { formatJobDeliveryLine } from '../../utils/jobMapsLink';
import { MapRouteSimulationPanel, MapRouteSimulationLayer } from '../../components/MapRouteSimulation';

const libraries = ['places'];
const googleMapsQueryUrl = (address) =>
  `https://maps.google.com/?q=${encodeURIComponent(address || '')}`;

const Map = () => {
  // Map-related constants (must be declared before useState that uses them)
  const defaultCenter = {
    lat: 47.6062, // Seattle area (adjust to your service area)
    lng: -122.3321,
  };

  const mapContainerStyle = {
    width: '100%',
    height: 'calc(100vh - 200px)',
    minHeight: '600px',
  };

  const [vehicles, setVehicles] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [depots, setDepots] = useState([]);
  const [geocodedDepots, setGeocodedDepots] = useState({});
  const [dataLoading, setDataLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [scriptError, setScriptError] = useState(false);
  const [mapLoading, setMapLoading] = useState(true); // Show loading until map is ready
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [markerType, setMarkerType] = useState('all'); // 'all', 'vehicles', 'jobs', 'depots'
  const [geocodedLocations, setGeocodedLocations] = useState({});
  const geocoderRef = useRef(null);
  const mapRef = useRef(null);
  const loadingTimeoutRef = useRef(null);
  const initialBoundsSetRef = useRef(false);
  const userHasChangedMapRef = useRef(false); // Track if user manually changed map
  const isUpdatingProgrammaticallyRef = useRef(false); // Track if we're updating programmatically
  const [mapCenter, setMapCenter] = useState(defaultCenter);
  const [mapZoom, setMapZoom] = useState(10);
  const [routeSimulation, setRouteSimulation] = useState(null);

  useEffect(() => {
    fetchData();
    // Refresh vehicle locations every 2 seconds to get updated GPS positions from drivers
    const interval = setInterval(() => {
      fetchData();
    }, 2000); // 2 seconds

    // Fallback timeout to hide loading if map doesn't load within 10 seconds
    loadingTimeoutRef.current = setTimeout(() => {
      setMapLoading((prev) => {
        if (prev) {
          console.warn('Map loading timeout - hiding loading overlay');
          return false;
        }
        return prev;
      });
    }, 10000);

    return () => {
      clearInterval(interval);
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (mapLoaded && jobs.length > 0 && window.google) {
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      geocodeJobLocations(jobs);
    }
  }, [mapLoaded, jobs]);

  useEffect(() => {
    if (mapLoaded && depots.length > 0 && window.google) {
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      geocodeDepotLocations(depots);
    }
  }, [mapLoaded, depots]);

  const geocodeDepotLocations = async (depotsData) => {
    if (!geocoderRef.current || !window.google || !depotsData?.length) return;
    const geocoder = geocoderRef.current;
    const additions = {};
    for (const d of depotsData) {
      if (d.latitude != null && d.longitude != null) continue;
      const addr = d.full_address;
      if (!addr) continue;
      try {
        const result = await new Promise((resolve, reject) => {
          geocoder.geocode({ address: addr }, (results, status) => {
            if (status === 'OK' && results[0]) {
              resolve(results[0].geometry.location);
            } else {
              reject(status);
            }
          });
        });
        additions[d.id] = {
          lat: result.lat(),
          lng: result.lng(),
        };
      } catch (err) {
        console.warn(`Failed to geocode depot ${addr}:`, err);
      }
    }
    if (Object.keys(additions).length > 0) {
      setGeocodedDepots((prev) => ({ ...prev, ...additions }));
    }
  };

  const getDepotPosition = (d) => {
    if (d.latitude != null && d.longitude != null) {
      return { lat: parseFloat(d.latitude), lng: parseFloat(d.longitude) };
    }
    return geocodedDepots[d.id] || null;
  };

  const fetchData = async () => {
    try {
      setError('');
      setDataLoading(true);

      // Fetch vehicles, jobs, and fuel depots in parallel
      const [vehiclesResponse, jobsResponse, depotsResponse] = await Promise.all([
        api.get('/vehicles/'),
        api.get('/managers/jobs/'),
        api.get('/depots/').catch(() => ({ data: [] })),
      ]);

      const vehiclesData = Array.isArray(vehiclesResponse.data)
        ? vehiclesResponse.data
        : vehiclesResponse.data?.results || [];

      const jobsData = Array.isArray(jobsResponse.data)
        ? jobsResponse.data
        : [];

      // Filter to only show active jobs (pending, accepted, active) - exclude complete and cancelled
      const activeJobsData = jobsData.filter(job => 
        ['pending', 'accepted', 'active'].includes(job.job_status)
      );

      const depotsData = Array.isArray(depotsResponse.data)
        ? depotsResponse.data
        : depotsResponse.data?.results || [];

      setVehicles(vehiclesData);
      setJobs(activeJobsData);
      setDepots(depotsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Failed to load map data');
    } finally {
      setDataLoading(false);
    }
  };

  const geocodeJobLocations = async (jobsData) => {
    if (!geocoderRef.current || !window.google) return;

    const geocoder = geocoderRef.current;
    const locations = {};

    for (const job of jobsData) {
      const address = formatJobDeliveryLine(job) || job.job_location;
      if (address && !geocodedLocations[job.id]) {
        try {
          const result = await new Promise((resolve, reject) => {
            geocoder.geocode({ address }, (results, status) => {
              if (status === 'OK' && results[0]) {
                resolve(results[0].geometry.location);
              } else {
                reject(status);
              }
            });
          });

          locations[job.id] = {
            lat: result.lat(),
            lng: result.lng(),
          };
        } catch (err) {
          console.error(`Failed to geocode ${address}:`, err);
        }
      }
    }

    if (Object.keys(locations).length > 0) {
      setGeocodedLocations((prev) => ({ ...prev, ...locations }));
    }
  };

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
    if (window.google) {
      geocoderRef.current = new window.google.maps.Geocoder();
    }
    setMapLoaded(true);
    setMapLoading(false); // Hide loading overlay when map is loaded
    
    // Clear the loading timeout since map loaded successfully
    if (loadingTimeoutRef.current) {
      clearTimeout(loadingTimeoutRef.current);
      loadingTimeoutRef.current = null;
    }
  }, []);

  // Memoized callbacks to prevent infinite update loops
  // After initial setup, we don't update state to avoid re-renders
  const handleCenterChanged = useCallback(() => {
    // Ignore if we're updating programmatically
    if (isUpdatingProgrammaticallyRef.current) {
      return;
    }
    // Only track user changes, don't update state (prevents infinite loop)
    if (mapRef.current && initialBoundsSetRef.current) {
      userHasChangedMapRef.current = true;
    }
  }, []);

  const handleZoomChanged = useCallback(() => {
    // Ignore if we're updating programmatically
    if (isUpdatingProgrammaticallyRef.current) {
      return;
    }
    // Only track user changes, don't update state (prevents infinite loop)
    if (mapRef.current && initialBoundsSetRef.current) {
      userHasChangedMapRef.current = true;
    }
  }, []);

  const getVehicleLocation = (vehicle) => {
    // First priority: Use vehicle's GPS coordinates (updated by driver)
    if (vehicle.latitude != null && vehicle.longitude != null) {
      return {
        lat: parseFloat(vehicle.latitude),
        lng: parseFloat(vehicle.longitude),
      };
    }
    // Fallback: If vehicle has a current job, use the job location
    if (vehicle.current_job) {
      const job = jobs.find((j) => j.id === vehicle.current_job);
      if (job && geocodedLocations[job.id]) {
        return geocodedLocations[job.id];
      }
    }
    // No location available
    return null;
  };

  const getFilteredMarkers = () => {
    const markers = [];

    if (markerType === 'all' || markerType === 'vehicles') {
      vehicles.forEach((vehicle) => {
        const location = getVehicleLocation(vehicle);
        if (location) {
          markers.push({
            id: `vehicle-${vehicle.id}`,
            type: 'vehicle',
            position: location,
            data: vehicle,
            title: `${vehicle.make} ${vehicle.model} (${vehicle.license_plate})`,
          });
        }
      });
    }

    if (markerType === 'all' || markerType === 'jobs') {
      jobs
        .filter((job) => ['pending', 'accepted', 'active'].includes(job.job_status)) // Only show active jobs
        .forEach((job) => {
          if (geocodedLocations[job.id]) {
            markers.push({
              id: `job-${job.id}`,
              type: 'job',
              position: geocodedLocations[job.id],
              data: job,
              title: `Job ${job.id.substring(0, 8)} - ${job.job_location}`,
            });
          }
        });
    }

    if (markerType === 'all' || markerType === 'depots') {
      depots.forEach((d) => {
        const pos = getDepotPosition(d);
        if (pos) {
          markers.push({
            id: `depot-${d.id}`,
            type: 'depot',
            position: pos,
            data: d,
            title: d.name,
          });
        }
      });
    }

    return markers;
  };

  const getMarkerIcon = (type) => {
    if (!window.google || !window.google.maps) return null;

    if (type === 'depot') {
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 10,
        fillColor: MAP_MARKER_COLORS.depot,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    }

    if (type === 'vehicle') {
      // Truck icon - top view: cab (smaller front) + cargo area (larger back)
      // Simple rectangular shapes that form a truck
      return {
        path: 'M-20,-8 L-5,-8 L-5,-4 L5,-4 L5,-8 L20,-8 L20,8 L-20,8 Z M-15,-3 L-8,-3 L-8,1 L-15,1 Z M8,-3 L15,-3 L15,3 L8,3 Z',
        fillColor: MAP_MARKER_COLORS.vehicle,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
        scale: 1,
        anchor: new window.google.maps.Point(0, 0),
      };
    } else {
      return {
        path: window.google.maps.SymbolPath.CIRCLE,
        scale: 8,
        fillColor: MAP_MARKER_COLORS.job,
        fillOpacity: 1,
        strokeColor: '#ffffff',
        strokeWeight: 2,
      };
    }
  };

  // Set initial map bounds/center only once when map first loads with data
  // Never change center/zoom if user has manually changed the map
  useEffect(() => {
    if (mapRef.current && mapLoaded && !initialBoundsSetRef.current && !userHasChangedMapRef.current) {
      const markers = getFilteredMarkers();
      
      // Set flag to prevent callbacks from firing during programmatic update
      isUpdatingProgrammaticallyRef.current = true;
      
      if (markers.length > 0) {
        // If we have markers, fit bounds to show all of them (only on initial load)
        const bounds = new window.google.maps.LatLngBounds();
        markers.forEach((marker) => {
          bounds.extend(marker.position);
        });
        mapRef.current.fitBounds(bounds);
        initialBoundsSetRef.current = true;
      } else {
        // If no markers, use default center (already set)
        initialBoundsSetRef.current = true;
      }
      
      // Reset flag after a short delay to allow map to update
      setTimeout(() => {
        isUpdatingProgrammaticallyRef.current = false;
      }, 100);
    }
  }, [mapLoaded, geocodedLocations, geocodedDepots, jobs, vehicles, depots, markerType]);

  const markers = getFilteredMarkers();

  return (
    <Layout>
      <Container>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Map View
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            View vehicles, active jobs, and fuel depots on the map
          </Typography>

          <ToggleButtonGroup
            value={markerType}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) setMarkerType(newValue);
            }}
            aria-label="marker type"
            sx={{ mb: 2 }}
          >
            <ToggleButton value="all" aria-label="all">
              All
            </ToggleButton>
            <ToggleButton value="vehicles" aria-label="vehicles">
              Vehicles
            </ToggleButton>
            <ToggleButton value="jobs" aria-label="jobs">
              Jobs
            </ToggleButton>
            <ToggleButton value="depots" aria-label="depots">
              Depots
            </ToggleButton>
          </ToggleButtonGroup>

          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Chip
              icon={<VehicleIcon />}
              label={`${vehicles.length} Vehicles`}
              color="primary"
              variant="outlined"
            />
            <Chip
              icon={<JobIcon />}
              label={`${jobs.filter(job => ['pending', 'accepted', 'active'].includes(job.job_status)).length} Active Jobs`}
              color="secondary"
              variant="outlined"
            />
            <Chip
              icon={<DepotIcon />}
              label={`${depots.length} Fuel Depots`}
              color="warning"
              variant="outlined"
            />
            <MapRouteSimulationPanel
              jobs={jobs}
              geocodedLocations={geocodedLocations}
              depots={depots}
              getDepotPosition={getDepotPosition}
              mapRef={mapRef}
              onSimulationChange={setRouteSimulation}
            />
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {!process.env.REACT_APP_GOOGLE_MAPS_API_KEY && !'AIzaSyCNRC6IxWlZZkx8HzTBctZydYIAchJUp9E' ? (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Google Maps API key is not configured. Please set REACT_APP_GOOGLE_MAPS_API_KEY in your environment variables.
          </Alert>
        ) : null}

        {scriptError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load Google Maps. Please check your API key and ensure the Maps JavaScript API is enabled.
          </Alert>
        ) : null}

        <Paper elevation={3} sx={{ overflow: 'hidden', position: 'relative' }}>
          {mapLoading && !scriptError && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                zIndex: 1000,
              }}
            >
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                Loading Google Maps...
              </Typography>
            </Box>
          )}
          <LoadScript
            googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyCNRC6IxWlZZkx8HzTBctZydYIAchJUp9E'}
            libraries={libraries}
            onLoad={() => {
              setScriptLoaded(true);
              // If script loads but map hasn't loaded yet, wait for map to load
              // mapLoading will be set to false in onMapLoad
            }}
            onError={() => {
              setScriptError(true);
              setMapLoading(false); // Hide loading overlay on script error
              setError('Failed to load Google Maps script. Please check your API key.');
            }}
          >
            {scriptError ? (
              <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px" p={3}>
                <Alert severity="error">
                  Failed to load Google Maps. Please check your API key and ensure the Maps JavaScript API is enabled in Google Cloud Console.
                </Alert>
              </Box>
            ) : scriptLoaded ? (
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                center={!initialBoundsSetRef.current ? mapCenter : undefined}
                zoom={!initialBoundsSetRef.current ? mapZoom : undefined}
                onLoad={onMapLoad}
                onCenterChanged={handleCenterChanged}
                onZoomChanged={handleZoomChanged}
                onError={() => {
                  setMapLoading(false);
                  setError('Failed to load Google Map. Please try refreshing the page.');
                }}
                options={{
                  disableDefaultUI: false,
                  zoomControl: true,
                  streetViewControl: false,
                  mapTypeControl: true,
                  fullscreenControl: true,
                }}
              >
                {markers.map((marker) => (
                  <Marker
                    key={marker.id}
                    position={marker.position}
                    title={marker.title}
                    icon={getMarkerIcon(marker.type)}
                    onClick={() => setSelectedMarker(marker)}
                  />
                ))}

                <MapRouteSimulationLayer simulation={routeSimulation} />

                {selectedMarker && (
                  <InfoWindow
                    position={selectedMarker.position}
                    onCloseClick={() => setSelectedMarker(null)}
                  >
                    <Box sx={{ p: 1, maxWidth: 250 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                        {selectedMarker.type === 'vehicle' ? (
                          <>
                            <VehicleIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            Vehicle
                          </>
                        ) : selectedMarker.type === 'depot' ? (
                          <>
                            <DepotIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            Fuel depot
                          </>
                        ) : (
                          <>
                            <JobIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                            Job
                          </>
                        )}
                      </Typography>
                      {selectedMarker.type === 'depot' ? (
                        <>
                          <Typography variant="body2">
                            <strong>{selectedMarker.data.name}</strong>
                          </Typography>
                          <Typography variant="body2">
                            <strong>Operator:</strong> {selectedMarker.data.parent_company}
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <a
                              href={googleMapsQueryUrl(selectedMarker.data.full_address)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {selectedMarker.data.full_address}
                            </a>
                          </Typography>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>
                            <strong>Fuels:</strong>{' '}
                            {[selectedMarker.data.has_diesel && 'Diesel', selectedMarker.data.has_def && 'DEF']
                              .filter(Boolean)
                              .join(', ')}
                          </Typography>
                          {(selectedMarker.data.contact_phone || selectedMarker.data.contact_email) && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>Contact:</strong>{' '}
                              {[selectedMarker.data.contact_name, selectedMarker.data.contact_phone, selectedMarker.data.contact_email]
                                .filter(Boolean)
                                .join(' · ')}
                            </Typography>
                          )}
                        </>
                      ) : selectedMarker.type === 'vehicle' ? (
                        <>
                          <Typography variant="body2">
                            <strong>License:</strong> {selectedMarker.data.license_plate}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Make/Model:</strong> {selectedMarker.data.make}{' '}
                            {selectedMarker.data.model}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Status:</strong> {selectedMarker.data.status}
                          </Typography>
                          {selectedMarker.data.driver_name && (
                            <Typography variant="body2">
                              <strong>Driver:</strong> {selectedMarker.data.driver_name}
                            </Typography>
                          )}
                          {selectedMarker.data.diesel_quantity != null && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>Diesel:</strong> {parseFloat(selectedMarker.data.diesel_quantity).toFixed(2)} gal
                            </Typography>
                          )}
                          {selectedMarker.data.def_quantity != null && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>DEF:</strong> {parseFloat(selectedMarker.data.def_quantity).toFixed(2)} gal
                            </Typography>
                          )}
                          {selectedMarker.data.latitude != null && selectedMarker.data.longitude != null && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>Location:</strong> {parseFloat(selectedMarker.data.latitude).toFixed(6)}, {parseFloat(selectedMarker.data.longitude).toFixed(6)}
                            </Typography>
                          )}
                          {selectedMarker.data.location_updated_at && (
                            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
                              Updated: {new Date(selectedMarker.data.location_updated_at).toLocaleString()}
                            </Typography>
                          )}
                          {selectedMarker.data.current_job && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              <strong>Current Job:</strong> {selectedMarker.data.current_job.substring(0, 8)}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <>
                          <Typography variant="body2">
                            <strong>Job ID:</strong> {selectedMarker.data.id.substring(0, 8)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Location:</strong>{' '}
                            <a
                              href={googleMapsQueryUrl(selectedMarker.data.job_location)}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              {selectedMarker.data.job_location}
                            </a>
                          </Typography>
                          <Typography variant="body2">
                            <strong>Status:</strong> {selectedMarker.data.job_status}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Customer:</strong> {selectedMarker.data.customer_name}
                          </Typography>
                          {selectedMarker.data.driver_name && (
                            <Typography variant="body2">
                              <strong>Driver:</strong> {selectedMarker.data.driver_name}
                            </Typography>
                          )}
                        </>
                      )}
                    </Box>
                  </InfoWindow>
                )}
              </GoogleMap>
            ) : null}
          </LoadScript>
        </Paper>
      </Container>
    </Layout>
  );
};

export default Map;

